import React, { useState } from 'react';
import './WebsiteBuilderPage.css';

const WebsiteBuilderPage = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [websiteData, setWebsiteData] = useState({
    title: '',
    description: '',
    pages: [],
    businessType: '',
    targetAudience: ''
  });
  const [activeStep, setActiveStep] = useState(1);

  const templates = [
    {
      id: 1,
      name: 'Business Pro',
      category: 'Business',
      image: 'https://via.placeholder.com/300x200/38bdf8/ffffff?text=Business+Pro',
      features: ['Professional Design', 'Contact Forms', 'SEO Optimized', 'Mobile Responsive']
    },
    {
      id: 2,
      name: 'E-commerce Plus',
      category: 'E-commerce',
      image: 'https://via.placeholder.com/300x200/ec4899/ffffff?text=E-commerce+Plus',
      features: ['Product Catalog', 'Shopping Cart', 'Payment Integration', 'Inventory Management']
    },
    {
      id: 3,
      name: 'Portfolio Elite',
      category: 'Portfolio',
      image: 'https://via.placeholder.com/300x200/10b981/ffffff?text=Portfolio+Elite',
      features: ['Gallery Showcase', 'Project Details', 'Client Testimonials', 'Social Media Integration']
    },
    {
      id: 4,
      name: 'Restaurant Deluxe',
      category: 'Food & Beverage',
      image: 'https://via.placeholder.com/300x200/f59e0b/ffffff?text=Restaurant+Deluxe',
      features: ['Menu Display', 'Online Ordering', 'Reservation System', 'Location Map']
    },
    {
      id: 5,
      name: 'Health & Wellness',
      category: 'Healthcare',
      image: 'https://via.placeholder.com/300x200/8b5cf6/ffffff?text=Health+Wellness',
      features: ['Service Listings', 'Appointment Booking', 'Health Resources', 'Patient Portal']
    },
    {
      id: 6,
      name: 'Real Estate Pro',
      category: 'Real Estate',
      image: 'https://via.placeholder.com/300x200/ef4444/ffffff?text=Real+Estate+Pro',
      features: ['Property Listings', 'Virtual Tours', 'Agent Profiles', 'Market Analysis']
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setActiveStep(2);
  };

  const handleDataChange = (e) => {
    setWebsiteData({
      ...websiteData,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateWebsite = () => {
    if (!selectedTemplate) {
      alert('Please select a template first');
      return;
    }
    
    if (!websiteData.title || !websiteData.businessType) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Here you would typically send the data to your backend to create the website
    console.log('Creating website with template:', selectedTemplate, 'and data:', websiteData);
    alert(`Website creation started with template: ${selectedTemplate.name}`);
    
    // Reset form
    setSelectedTemplate(null);
    setWebsiteData({
      title: '',
      description: '',
      pages: [],
      businessType: '',
      targetAudience: ''
    });
    setActiveStep(1);
  };

  const steps = [
    { id: 1, title: 'Choose Template' },
    { id: 2, title: 'Customize' },
    { id: 3, title: 'Review & Create' }
  ];

  return (
    <section className="website-builder-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Website Builder</h1>
          <p className="page-subtitle">Create your professional website in just a few simple steps</p>
        </div>
        
        <div className="builder-container">
          {/* Progress Steps */}
          <div className="progress-steps">
            {steps.map((step) => (
              <div 
                key={step.id} 
                className={`step ${activeStep === step.id ? 'active' : ''} ${activeStep > step.id ? 'completed' : ''}`}
              >
                <div className="step-number">{step.id}</div>
                <div className="step-title">{step.title}</div>
              </div>
            ))}
          </div>
          
          {/* Step 1: Choose Template */}
          {activeStep === 1 && (
            <div className="builder-step">
              <h2 className="step-title">Choose a Template</h2>
              <p className="step-description">Select the perfect template for your business needs</p>
              
              <div className="templates-grid">
                {templates.map((template) => (
                  <div 
                    key={template.id}
                    className={`template-card glass-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="template-image-container">
                      <img src={template.image} alt={template.name} className="template-image" />
                    </div>
                    <div className="template-info">
                      <h3 className="template-name">{template.name}</h3>
                      <p className="template-category">{template.category}</p>
                      <ul className="template-features">
                        {template.features.map((feature, index) => (
                          <li key={index}>
                            <i className="fas fa-check-circle"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Step 2: Customize Website */}
          {activeStep === 2 && selectedTemplate && (
            <div className="builder-step">
              <div className="step-header">
                <h2 className="step-title">Customize Your Website</h2>
                <p className="step-description">Personalize your {selectedTemplate.name} template</p>
                <div className="selected-template-preview">
                  <img src={selectedTemplate.image} alt={selectedTemplate.name} className="preview-image" />
                  <div className="template-details">
                    <h3>{selectedTemplate.name}</h3>
                    <p>{selectedTemplate.category}</p>
                  </div>
                </div>
              </div>
              
              <div className="customization-form glass-card">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="title">Website Title <span className="required">*</span></label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={websiteData.title}
                      onChange={handleDataChange}
                      placeholder="Enter your website title"
                      className="form-control"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="businessType">Business Type <span className="required">*</span></label>
                    <input
                      type="text"
                      id="businessType"
                      name="businessType"
                      value={websiteData.businessType}
                      onChange={handleDataChange}
                      placeholder="e.g., Restaurant, Consulting, E-commerce"
                      className="form-control"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="targetAudience">Target Audience</label>
                    <input
                      type="text"
                      id="targetAudience"
                      name="targetAudience"
                      value={websiteData.targetAudience}
                      onChange={handleDataChange}
                      placeholder="e.g., Young Professionals, Families, Businesses"
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label htmlFor="description">Website Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={websiteData.description}
                      onChange={handleDataChange}
                      placeholder="Describe your website and business"
                      rows="4"
                      className="form-control"
                    ></textarea>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="glass-button prev-button"
                    onClick={() => setActiveStep(1)}
                  >
                    <i className="fas fa-arrow-left"></i> Back to Templates
                  </button>
                  <button 
                    className="glass-button next-button"
                    onClick={() => setActiveStep(3)}
                  >
                    Review & Create <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Review & Create */}
          {activeStep === 3 && selectedTemplate && (
            <div className="builder-step">
              <h2 className="step-title">Review & Create</h2>
              <p className="step-description">Review your website details before creation</p>
              
              <div className="review-section">
                <div className="review-card glass-card">
                  <h3>Selected Template</h3>
                  <div className="template-review">
                    <img src={selectedTemplate.image} alt={selectedTemplate.name} className="review-image" />
                    <div className="template-details">
                      <h4>{selectedTemplate.name}</h4>
                      <p>{selectedTemplate.category}</p>
                    </div>
                  </div>
                </div>
                
                <div className="review-card glass-card">
                  <h3>Website Details</h3>
                  <div className="website-details">
                    <p><strong>Title:</strong> {websiteData.title}</p>
                    <p><strong>Business Type:</strong> {websiteData.businessType}</p>
                    <p><strong>Target Audience:</strong> {websiteData.targetAudience || 'Not specified'}</p>
                    <p><strong>Description:</strong> {websiteData.description || 'Not provided'}</p>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="glass-button prev-button"
                    onClick={() => setActiveStep(2)}
                  >
                    <i className="fas fa-arrow-left"></i> Back to Customize
                  </button>
                  <button 
                    className="glass-button create-button"
                    onClick={handleCreateWebsite}
                  >
                    <i className="fas fa-magic"></i> Create Website
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WebsiteBuilderPage;